import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useServiceOS } from '../hooks/useServiceOS';
import {
  ArrowLeft,
  EnvelopeSimple,
  Phone,
  MapPin,
  Clock,
  CalendarCheck,
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  ArrowsClockwise,
  Sparkle,
  Copy,
  PencilSimple,
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  new: 'bg-[#14F195]/10 text-[#14F195] border-[#14F195]/30',
  contacted: 'bg-[#6366F1]/10 text-[#6366F1] border-[#6366F1]/30',
  awaiting_reply: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30',
  booked: 'bg-[#22C55E]/10 text-[#22C55E] border-[#22C55E]/30',
  lost: 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30',
};

const SOURCE_BADGE = {
  email: 'bg-[#3B82F6]/10 text-[#3B82F6]',
  manual: 'bg-[#8B5CF6]/10 text-[#8B5CF6]',
  test: 'bg-[#6B7280]/10 text-[#6B7280]',
  website: 'bg-[#F97316]/10 text-[#F97316]',
};

export default function SOSLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const {
    getLead, markBooked, markLost, generateReply, approveReply, sendReply, createJob,
  } = useServiceOS();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [showBookModal, setShowBookModal] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const res = await getLead(leadId);
      setData(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getLead, leadId]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const handleAction = async (action, ...args) => {
    setActionLoading(action);
    try {
      await { markBooked, markLost, generateReply, approveReply, sendReply }[action](...args);
      await fetchLead();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading('');
    }
  };

  const handleBookJob = async (window) => {
    setActionLoading('book');
    try {
      await createJob({
        lead_id: leadId,
        customer_name: data.lead.customer_name || 'Customer',
        customer_email: data.lead.customer_email,
        customer_phone: data.lead.customer_phone,
        service_type: data.lead.service_type,
        location_text: data.lead.location_text,
        scheduled_date: window.date,
        start_time: window.start_time,
        end_time: window.end_time,
        window_label: window.label,
      });
      await fetchLead();
      setShowBookModal(false);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="lead-detail-loading">
        <div className="w-5 h-5 border-2 border-[#14F195] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="text-[#8B8FA3] text-center py-16">Lead not found</p>;

  const { lead, activities, replies, followups, availability } = data;
  const latestDraft = replies.find(r => r.status === 'draft');

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="sos-lead-detail">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/service-os/leads')} className="text-[#8B8FA3] hover:text-white" data-testid="lead-back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white">{lead.customer_name || 'Unknown lead'}</h1>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_COLORS[lead.status]}`}>
              {lead.status}
            </span>
          </div>
          <p className="text-xs text-[#8B8FA3]">Created {new Date(lead.created_at).toLocaleDateString()}</p>
        </div>

        {/* Actions */}
        {lead.status !== 'booked' && lead.status !== 'lost' && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowBookModal(true)}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#22C55E]/10 text-[#22C55E] rounded-lg text-xs font-medium hover:bg-[#22C55E]/20 transition-colors"
              data-testid="lead-book-btn"
            >
              <CalendarCheck size={14} /> Book job
            </button>
            <button
              onClick={() => handleAction('markLost', leadId, 'no_response')}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#EF4444]/10 text-[#EF4444] rounded-lg text-xs font-medium hover:bg-[#EF4444]/20 transition-colors"
              data-testid="lead-lost-btn"
            >
              <XCircle size={14} /> Mark lost
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer info */}
          <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="lead-customer-info">
            <h2 className="text-sm font-semibold text-white mb-3">Customer details</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {lead.customer_email && (
                <div className="flex items-center gap-2 text-[#C4C9D4]">
                  <EnvelopeSimple size={14} className="text-[#8B8FA3]" /> {lead.customer_email}
                </div>
              )}
              {lead.customer_phone && (
                <div className="flex items-center gap-2 text-[#C4C9D4]">
                  <Phone size={14} className="text-[#8B8FA3]" /> {lead.customer_phone}
                </div>
              )}
              {lead.location_text && (
                <div className="flex items-center gap-2 text-[#C4C9D4]">
                  <MapPin size={14} className="text-[#8B8FA3]" /> {lead.location_text}
                </div>
              )}
              {lead.service_type && (
                <div className="flex items-center gap-2 text-[#C4C9D4]">
                  <Sparkle size={14} className="text-[#8B8FA3]" /> {lead.service_type}
                </div>
              )}
            </div>
            {lead.enquiry_summary && (
              <div className="mt-4 p-3 bg-[#0B0C10] rounded-lg">
                <p className="text-xs text-[#8B8FA3] mb-1">Enquiry summary</p>
                <p className="text-sm text-[#E2E8F0]">{lead.enquiry_summary}</p>
              </div>
            )}
          </div>

          {/* Reply draft */}
          {replies.length > 0 && (
            <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="lead-reply-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-white">Reply draft</h2>
                <button
                  onClick={() => handleAction('generateReply', leadId)}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1 text-xs text-[#6366F1] hover:underline"
                  data-testid="lead-regenerate-reply"
                >
                  <ArrowsClockwise size={12} /> Regenerate
                </button>
              </div>
              {replies.slice(0, 1).map(r => (
                <div key={r.id} className="space-y-3">
                  <div className="p-3 bg-[#0B0C10] rounded-lg">
                    <p className="text-xs text-[#8B8FA3] mb-1">Subject: {r.subject}</p>
                    <p className="text-sm text-[#E2E8F0] whitespace-pre-line">{r.body}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      r.status === 'sent' ? 'bg-[#22C55E]/10 text-[#22C55E]' :
                      r.status === 'approved' ? 'bg-[#6366F1]/10 text-[#6366F1]' :
                      'bg-[#F59E0B]/10 text-[#F59E0B]'
                    }`}>{r.status}</span>
                    {r.status === 'draft' && (
                      <>
                        <button
                          onClick={() => handleAction('approveReply', r.id)}
                          disabled={!!actionLoading}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#6366F1]/10 text-[#6366F1] rounded-lg text-xs hover:bg-[#6366F1]/20"
                          data-testid="lead-approve-reply"
                        >
                          <CheckCircle size={12} /> Approve
                        </button>
                        <button
                          onClick={() => handleAction('sendReply', r.id)}
                          disabled={!!actionLoading}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#14F195]/10 text-[#14F195] rounded-lg text-xs hover:bg-[#14F195]/20"
                          data-testid="lead-send-reply"
                        >
                          <PaperPlaneTilt size={12} /> Send
                        </button>
                      </>
                    )}
                    {r.status === 'approved' && (
                      <button
                        onClick={() => handleAction('sendReply', r.id)}
                        disabled={!!actionLoading}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#14F195]/10 text-[#14F195] rounded-lg text-xs hover:bg-[#14F195]/20"
                        data-testid="lead-send-reply"
                      >
                        <PaperPlaneTilt size={12} /> Send
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Activity timeline */}
          <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="lead-activity">
            <h2 className="text-sm font-semibold text-white mb-3">Activity</h2>
            {activities.length === 0 ? (
              <p className="text-sm text-[#8B8FA3]">No activity yet</p>
            ) : (
              <div className="space-y-3">
                {activities.map((a, i) => (
                  <div key={a.id || i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#6366F1] mt-2 shrink-0" />
                    <div>
                      <p className="text-sm text-white">{a.title}</p>
                      {a.description && <p className="text-xs text-[#8B8FA3]">{a.description}</p>}
                      <p className="text-[10px] text-[#555] mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Availability */}
          <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="lead-availability">
            <h2 className="text-sm font-semibold text-white mb-3">Availability</h2>
            {availability.length === 0 ? (
              <p className="text-xs text-[#8B8FA3]">No windows available</p>
            ) : (
              <div className="space-y-2">
                {availability.map((w, i) => (
                  <div key={w.id || w.start || w.label || `avail-${i}`} className="flex items-center gap-2 px-3 py-2 bg-[#0B0C10] rounded-lg">
                    <Clock size={14} className="text-[#14F195] shrink-0" />
                    <span className="text-xs text-[#E2E8F0]">{w.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Follow-ups */}
          <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="lead-followups">
            <h2 className="text-sm font-semibold text-white mb-3">Follow-ups</h2>
            {followups.length === 0 ? (
              <p className="text-xs text-[#8B8FA3]">No follow-ups scheduled</p>
            ) : (
              <div className="space-y-2">
                {followups.map(fu => (
                  <div key={fu.id} className="flex items-center justify-between px-3 py-2 bg-[#0B0C10] rounded-lg">
                    <div>
                      <p className="text-xs text-[#E2E8F0]">Follow-up #{fu.sequence_number}</p>
                      <p className="text-[10px] text-[#555]">{new Date(fu.scheduled_for).toLocaleDateString()}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      fu.status === 'pending' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' :
                      fu.status === 'cancelled' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                      'bg-[#22C55E]/10 text-[#22C55E]'
                    }`}>{fu.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick info */}
          <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-5" data-testid="lead-quick-info">
            <h2 className="text-sm font-semibold text-white mb-3">Details</h2>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-[#8B8FA3]">Source</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${SOURCE_BADGE[lead.source_type] || 'bg-[#1E1F2E] text-[#8B8FA3]'}`} data-testid="lead-source-badge">{lead.source_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8B8FA3]">Urgency</span>
                <span className="text-[#E2E8F0] capitalize">{lead.urgency}</span>
              </div>
              {lead.extraction_confidence > 0 && (
                <div className="flex justify-between">
                  <span className="text-[#8B8FA3]">AI confidence</span>
                  <span className="text-[#E2E8F0]">{Math.round(lead.extraction_confidence * 100)}%</span>
                </div>
              )}
              {lead.booked_job_id && (
                <div className="flex justify-between">
                  <span className="text-[#8B8FA3]">Job</span>
                  <Link to="/service-os/jobs" className="text-[#14F195] hover:underline">View job</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Book job modal */}
      {showBookModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="book-job-modal">
          <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-white mb-2">Book a job</h2>
            <p className="text-xs text-[#8B8FA3] mb-4">Select an available window</p>
            {availability.length === 0 ? (
              <p className="text-sm text-[#8B8FA3]">No availability windows</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availability.map((w, i) => (
                  <button
                    key={w.id || w.start || w.label || `book-window-${i}`}
                    onClick={() => handleBookJob(w)}
                    disabled={actionLoading === 'book'}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg hover:border-[#14F195]/40 text-left transition-colors"
                    data-testid={`book-window-${i}`}
                  >
                    <CalendarCheck size={16} className="text-[#14F195]" />
                    <span className="text-sm text-[#E2E8F0]">{w.label}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowBookModal(false)} className="px-4 py-2 text-sm text-[#8B8FA3] hover:text-white">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
